export const WIRE_TRANSPORT = {
  HTTP: 'http',
} as const;

export type WireTransport =
  (typeof WIRE_TRANSPORT)[keyof typeof WIRE_TRANSPORT];

export const WIRE_INTERACTION = {
  UNARY: 'unary',
  SERVER_STREAMING: 'server_streaming',
  CLIENT_STREAMING: 'client_streaming',
  BIDI: 'bidi',
} as const;

export type WireInteraction =
  (typeof WIRE_INTERACTION)[keyof typeof WIRE_INTERACTION];

export const WIRE_SERIALIZATION = {
  PROTOBUF: 'protobuf',
  JSON: 'json',
} as const;

export type WireSerialization =
  (typeof WIRE_SERIALIZATION)[keyof typeof WIRE_SERIALIZATION];

export const WIRE_FRAMING = {
  GRPC_WEB: 'grpc-web',
  GRPC_WEB_TEXT: 'grpc-web-text',
  CONNECT: 'connect',
  CONNECT_STREAMING: 'connect-streaming',
} as const;

export type WireFraming = (typeof WIRE_FRAMING)[keyof typeof WIRE_FRAMING];

export const WIRE_PROTOCOL_FAMILY = {
  GRPC_WEB: 'grpc-web',
  CONNECT: 'connect',
} as const;

export type WireProtocolFamily =
  (typeof WIRE_PROTOCOL_FAMILY)[keyof typeof WIRE_PROTOCOL_FAMILY];

export type WireProfile = {
  family: WireProtocolFamily;
  transport: WireTransport;
  interaction: WireInteraction;
  serialization: WireSerialization;
  framing: WireFraming;
  serviceName?: string;
  methodName?: string;
  contentType?: string;
};

export type ProtocolCapabilities = {
  capture: boolean;
  decode: boolean;
  replay: boolean;
  activeProbe: boolean;
};

export const UNSUPPORTED_PROTOCOL_CAPABILITIES: ProtocolCapabilities = {
  capture: false,
  decode: false,
  replay: false,
  activeProbe: false,
};

export const GRPC_WEB_CONNECT_CAPABILITIES: ProtocolCapabilities = {
  capture: true,
  decode: true,
  replay: true,
  activeProbe: true,
};

export type WireHeaderList = ReadonlyArray<{
  name?: string | null;
  value?: string | null;
}>;

export type WireHeaderMap = Record<string, string | string[] | undefined>;

export type WireDetectInput = {
  url?: string;
  method?: string;
  requestHeaders?: WireHeaderList | WireHeaderMap;
  responseHeaders?: WireHeaderList | WireHeaderMap;
  requestContentType?: string;
  responseContentType?: string;
};

export type WireDetector = {
  id: string;
  detect: (input: WireDetectInput) => WireProfile | null;
  capabilities: ProtocolCapabilities;
};

const detectors: WireDetector[] = [];

export function registerWireDetector(detector: WireDetector): void {
  const existingIndex = detectors.findIndex(
    (entry) => entry.id === detector.id,
  );

  if (existingIndex >= 0) {
    detectors[existingIndex] = detector;

    return;
  }

  detectors.push(detector);
}

export function listWireDetectors(): readonly WireDetector[] {
  return detectors;
}

export function detectWireProfile(input: WireDetectInput): WireProfile | null {
  for (const detector of detectors) {
    const profile = detector.detect(input);

    if (profile) {
      return profile;
    }
  }

  return null;
}

export function getWireCapabilities(
  profile: WireProfile | null | undefined,
): ProtocolCapabilities {
  if (!profile) {
    return UNSUPPORTED_PROTOCOL_CAPABILITIES;
  }

  const detector = detectors.find((entry) => entry.id === profile.family);

  return detector?.capabilities ?? GRPC_WEB_CONNECT_CAPABILITIES;
}

export function resetWireDetectorsForTests(): void {
  detectors.splice(0, detectors.length);
}
